'use client'

import type { BuilderContent } from '@builder.io/sdk'
import { usePathname, useSearchParams } from 'next/navigation'
import { createContext, use, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useOnMount } from '@src/hooks/useOnMount'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { ServerActions } from '@src/services/server-actions.service'
import { useIdentify } from '@src/utils/analytics.util'
import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { processCart } from '@src/utils/cart.util'
import { QUERY_PARAMS } from '@src/utils/constants.util'
import { hash } from '@src/utils/hash.util'
import { log } from '@src/utils/log.util'
import { safeQueryParamParse } from '@src/utils/string.util'

interface ContextType {
  cart: Commercetools.Cart | null
  setCart: (cart: Commercetools.Cart | null) => void
  shippingMethods: Commercetools.ShippingMethod[]
  setShippingMethods: (shippingMethods: Commercetools.ShippingMethod[]) => void
  isLoading: boolean
  cartDrawerBanner: BuilderContent | undefined
}

const CartContext = createContext<ContextType>({
  cart: null,
  setCart: () => {},
  shippingMethods: [],
  setShippingMethods: () => {},
  isLoading: false,
  cartDrawerBanner: {},
})

/** Used to provide the cart to client components **/
export function CartProvider(props: {
  children: React.ReactNode
  cart: ContextType['cart']
  shippingMethods: ContextType['shippingMethods']
  cartDrawerBanner: Promise<BuilderContent>
}) {
  const { locale } = useLocale()
  const { user } = useSession()
  const [cart, setCart] = useState(props.cart)
  const { live } = getBuilderPreviewMode()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isBulkUpdating = searchParams?.has(QUERY_PARAMS.bulk)
  const isInitiallyLoading = Boolean(!props.cart || isBulkUpdating)
  const [isMounted, setIsMounted] = useState(false)
  const [isLoading, setLoading] = useState(isInitiallyLoading)
  const [shippingMethods, setShippingMethods] = useState(props.shippingMethods)
  const cartDrawerBanner = use(props.cartDrawerBanner)

  const cartProcessed = useMemo(() => {
    return processCart(cart)
  }, [cart])

  const loadCart = useCallback(
    async function loadCart() {
      if (!live) return

      // Skip cart creation on SSE/account pages
      const isAccountPage = pathname?.includes('/account/')
      if (isAccountPage) {
        setCart(await ServerActions.serial.getCart())
        log.info('[useCart] Skipping cart creation on account/SSE page')
        setLoading(false)
        return
      }

      setLoading(true)

      // Get or create cart if one wasn't provided as a prop
      if (!props.cart) {
        try {
          setCart(await ServerActions.parallel.getOrCreateCart())
        } catch (error: any) {
          log.warn(`[useCart] Failed to get cart. Creating a new one. - ${error?.message}`, error)
          setCart(await ServerActions.parallel.createCart())
        }
      }

      // Bulk update cart
      if (isBulkUpdating) {
        const args = safeQueryParamParse<Parameters<typeof ServerActions.serial.bulkUpdateCart>[0]>(
          searchParams?.get(QUERY_PARAMS.bulk)
        )
        if (args) {
          setCart(await ServerActions.serial.bulkUpdateCart(args))
          const url = new URL(window.location.href)
          url.searchParams.delete(QUERY_PARAMS.bulk)
          window.history.replaceState({}, '', url.toString())
        }
      }

      setLoading(false)
    },
    [props.cart, live, pathname, searchParams, isBulkUpdating]
  )

  useOnMount(async () => {
    setIsMounted(true)
    loadCart()
  })

  // Check cart is created after navigating to a page
  useEffect(() => {
    if (isMounted && !cart && !isLoading) {
      log.info('[useCart] Cart is not created, creating a new one')
      loadCart()
    }
  }, [isMounted, isLoading, cart, loadCart, pathname])

  // Change cart store when country code changes in SPA navigation
  useEffect(() => {
    async function setCartStore() {
      if (!cart || cart.country === locale.countryCode) return

      log.info(`[useCart] Changing cart store to ${locale.countryCode}`)
      setLoading(true)
      setCart(null) // ensure nothing else is using the cart
      const cartUpdated = await ServerActions.serial.getOrCreateCart()
      setCart(cartUpdated)
      setLoading(false)
    }
    void setCartStore()
  }, [locale.countryCode, cart])

  // Set cart locale
  useEffect(() => {
    async function setCartLocale() {
      if (!cart || cart.locale === locale.code) return
      log.info(`[useCart] Setting ${cart.country} cart locale to ${locale.code}`)
      setLoading(true)
      const cartUpdated = await ServerActions.serial.setCartLocale({ locale: locale.code })
      setCart(cartUpdated)
      setLoading(false)
    }

    void setCartLocale()
  }, [locale.code, cart])

  useEffect(() => {
    async function syncAuthEmail() {
      if (!cart || !user?.email || cart.customerEmail === user?.email) return
      log.info(`[useCart] Setting session email to cart customerEmail`)
      const res = await ServerActions.serial.setCustomerEmail(user?.email)
      setCart((cart) => res.cart ?? cart)
    }
    syncAuthEmail()
  }, [cart, user])

  const hashes = useMemo(async () => {
    const data = {
      email: cart?.customerEmail ? await hash(cart.customerEmail.toLowerCase()) : undefined,
    }
    return data
  }, [cart?.customerEmail])

  useIdentify({ hashes, 'cart id': cart?.id, 'anonymous id': cart?.anonymousId }, [
    cart?.customerEmail,
  ])

  return (
    <CartContext.Provider
      value={{
        cart: cartProcessed,
        isLoading,
        setCart,
        shippingMethods,
        setShippingMethods,
        cartDrawerBanner,
      }}
    >
      {props.children}
    </CartContext.Provider>
  )
}

/** Used to access and update the cart across client components **/
export function useCart() {
  try {
    return useContext(CartContext)
  } catch (e) {
    throw new Error('useCart must be used within a CartProvider')
  }
}
